import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Panel } from "@/components/ui/Panel";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { MonoText } from "@/components/ui/CopyButton";
import { LoadingState, ErrorState } from "@/components/ui/States";
import { formatIDR, formatDateTime } from "@/lib/format";

interface AdminOrder {
  id: string;
  user_id: string;
  variant_id: string;
  amount: number;
  gateway_trx_id: string | null;
  status: string;
  webhook_processed_at: string | null;
  expires_at: string | null;
  created_at: string;
}

export function AdminOrdersPage() {
  const { user, loading: authLoading } = useAuth();
  const [orders, setOrders] = useState<AdminOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.listOrdersAdmin(200);
      setOrders(data as AdminOrder[]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load orders");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && user?.role === "owner") load();
  }, [user, authLoading, load]);

  // Auto-refresh every 5s jika diaktifkan
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [autoRefresh, load]);

  if (authLoading) return <LoadingState />;
  if (!user || user.role !== "owner") {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center">
        <MonoText size="sm" color="status-failed">[ ACCESS_DENIED ]</MonoText>
      </div>
    );
  }

  const paidCount = orders.filter((o) => o.status === "PAID").length;
  const pendingCount = orders.filter((o) => o.status === "PENDING").length;
  const totalRevenue = orders
    .filter((o) => o.status === "PAID")
    .reduce((sum, o) => sum + o.amount, 0);

  return (
    <div className="max-w-7xl mx-auto px-4 py-10">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="section-heading">TRANSACTION_LOG</h1>
          <h2 className="mt-3 font-sans text-3xl text-ink-primary">Order Audit Trail</h2>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 cursor-pointer font-mono text-2xs uppercase tracking-widest text-ink-muted">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="w-3.5 h-3.5 accent-terminal border-border bg-void"
            />
            auto-refresh 5s
          </label>
          <Button variant="ghost" onClick={load}>[ REFRESH ]</Button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="panel p-4">
          <MonoText size="xs" color="ink-dim">TOTAL_ORDERS</MonoText>
          <div className="mt-1 font-mono text-2xl text-ink-primary tabular-nums">{orders.length}</div>
        </div>
        <div className="panel p-4">
          <MonoText size="xs" color="ink-dim">PAID</MonoText>
          <div className="mt-1 font-mono text-2xl text-terminal tabular-nums">{paidCount}</div>
        </div>
        <div className="panel p-4">
          <MonoText size="xs" color="ink-dim">PENDING</MonoText>
          <div className="mt-1 font-mono text-2xl text-status-pending tabular-nums">{pendingCount}</div>
        </div>
        <div className="panel p-4 border-terminal">
          <MonoText size="xs" color="ink-dim">REVENUE</MonoText>
          <div className="mt-1 font-mono text-xl text-terminal tabular-nums">{formatIDR(totalRevenue)}</div>
        </div>
      </div>

      {error ? (
        <ErrorState error={error} onRetry={load} />
      ) : loading && orders.length === 0 ? (
        <LoadingState label="FETCHING_ORDERS" />
      ) : orders.length === 0 ? (
        <Panel>
          <MonoText size="sm" color="ink-muted">NO_ORDERS_YET</MonoText>
        </Panel>
      ) : (
        <Panel padded={false}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-elevated">
                  <th className="text-left font-mono text-2xs uppercase tracking-widest text-ink-muted px-4 py-2">ORDER_ID</th>
                  <th className="text-left font-mono text-2xs uppercase tracking-widest text-ink-muted px-4 py-2">USER</th>
                  <th className="text-right font-mono text-2xs uppercase tracking-widest text-ink-muted px-4 py-2">AMOUNT</th>
                  <th className="text-left font-mono text-2xs uppercase tracking-widest text-ink-muted px-4 py-2">STATUS</th>
                  <th className="text-left font-mono text-2xs uppercase tracking-widest text-ink-muted px-4 py-2">CREATED</th>
                  <th className="text-left font-mono text-2xs uppercase tracking-widest text-ink-muted px-4 py-2">PROCESSED</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} className="border-b border-border last:border-b-0 hover:bg-elevated">
                    <td className="px-4 py-3 font-mono text-xs text-terminal">{o.id}</td>
                    <td className="px-4 py-3 font-mono text-xs text-ink-muted">
                      {o.user_id.slice(0, 12)}…
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-xs text-ink-primary tabular-nums">
                      {formatIDR(o.amount)}
                    </td>
                    <td className="px-4 py-3">
                      <OrderStatusBadge status={o.status} />
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-ink-muted">
                      {formatDateTime(o.created_at)}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-ink-muted">
                      {o.webhook_processed_at ? formatDateTime(o.webhook_processed_at) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}
    </div>
  );
}

function OrderStatusBadge({ status }: { status: string }) {
  const variant =
    status === "PAID" ? "active"
    : status === "PENDING" ? "pending"
    : status === "FAILED" ? "failed"
    : "expired";
  return <Badge variant={variant as "active" | "pending" | "failed" | "expired"}>{status}</Badge>;
}